import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { getSelectedCustomer } from '../../../../../../_augmentt/selectors/customers'
import { DATA_AVAILABILITY_REASON, DATA_KEYS, SHADOW_AI_LOOKBACK_DAYS, TONE } from '../../data/constants'
import { getDataGap } from '../../data/reportModel'
import SaaSApplicationLogo from '../../../../../partials/content/SaaSApplicationLogo'
import ApplicationDetailsModal from '../../../modals/ApplicationDetailsModal'
import EmptyState from '../../components/EmptyState/EmptyState'
import ReportTable from '../../components/ReportTable/ReportTable'
import { useAIRiskReportExportMode } from '../../components/RenderMode/AIRiskReportRenderModeProvider'
import SubArea from '../../components/SubArea/SubArea'
import SubBlock from '../../components/SubBlock/SubBlock'
import ApprovalStatusCell from './ApprovalStatusCell'
import { BreachCell, DaysActiveCell, SecurityCell } from './cellFormatters'
import { getDiscoverUnavailableMessageId } from './discoverAvailability'
import ShadowAiEmptyState from './ShadowAiEmptyState'
import ShadowAiUsers from './ShadowAiUsers'
import { buildShadowAiStats } from './shadowAiStats'
import styles from './ShadowAi.module.scss'

const getShadowAiDateRange = () => {
  const endDate = new Date()
  const startDate = new Date(endDate)
  startDate.setUTCHours(0, 0, 0, 0)
  startDate.setUTCDate(startDate.getUTCDate() - (SHADOW_AI_LOOKBACK_DAYS - 1))

  return { startDate: startDate.toISOString(), endDate: endDate.toISOString() }
}

const getAppDisplayName = (app) => app.name || app.url || '-'

const buildColumns = (onOpenApp, onOpenAppUsers, isExportMode) => [
  {
    key: 'app',
    header: <FormattedMessage id="AI_RISK_REPORT.COL.APPLICATION" />,
    // Static (non-clickable) name in export mode — the modal it opens has no meaning in a PDF.
    render: (row) => {
      const displayName = getAppDisplayName(row)
      return (
        <span className={styles.appNameCell}>
          <SaaSApplicationLogo domain={row.url} cell={displayName} />
          {isExportMode || !row.url ? (
            <span className={styles.appNameStatic}>{displayName}</span>
          ) : (
            <button type="button" className={styles.appNameButton} onClick={() => onOpenApp(row)}>
              {displayName}
            </button>
          )}
        </span>
      )
    },
    sortable: true,
    sortValue: (row) => getAppDisplayName(row).toLowerCase(),
  },
  {
    key: 'sanctioned',
    header: <FormattedMessage id="AI_RISK_REPORT.THIRD.SHADOW_AI.COL_SANCTIONED" />,
    render: (row) => <ApprovalStatusCell row={row} />,
    sortable: true,
    sortValue: (row) => row.sanctioned || '',
  },
  {
    key: 'security',
    header: <FormattedMessage id="AI_RISK_REPORT.THIRD.SHADOW_AI.COL_SECURITY" />,
    render: (row) => <SecurityCell ssoSupport={row.ssoSupport} mfaSupport={row.mfaSupport} />,
  },
  {
    key: 'latestBreach',
    header: <FormattedMessage id="AI_RISK_REPORT.THIRD.SHADOW_AI.COL_LATEST_BREACH" />,
    render: (row) => <BreachCell latestBreach={row.latestBreach} breachUrl={row.breachUrl} />,
    sortable: true,
    sortValue: (row) => row.latestBreach || '',
  },
  {
    key: 'users',
    header: <FormattedMessage id="AI_RISK_REPORT.THIRD.SHADOW_AI.COL_USERS" />,
    // The count opens the same app details modal as the name, but on the Users tab — mirroring the
    // Discover applications table, where clicking a user count deep-links into that app's users.
    // Static in export mode.
    render: (row) => {
      if (row.users === null || row.users === undefined) return <span className={styles.usersValue}>-</span>
      if (isExportMode || row.users === 0) return <span className={styles.usersValue}>{row.users}</span>
      return (
        <button type="button" className={styles.usersButton} onClick={() => onOpenAppUsers(row)}>
          {row.users}
        </button>
      )
    },
    sortable: true,
    sortValue: (row) => row.users ?? 0,
  },
  {
    key: 'daysActive',
    header: <FormattedMessage id="AI_RISK_REPORT.THIRD.SHADOW_AI.COL_USERS_ACTIVE" />,
    render: (row) => <DaysActiveCell dailyActivity={row.dailyActivity} totalUsers={row.users ?? 0} />,
  },
]

export default function ShadowAi({
  appsData = null,
  usersData = null,
  score = { value: null, tone: TONE.NEUTRAL },
  gap = null,
  dataAvailability = [],
}) {
  const selectedCustomer = useSelector(getSelectedCustomer)
  // `openOnUsers` deep-links the modal to its Users tab (count clicked) vs. Details tab (name clicked).
  const [modalState, setModalState] = useState(null)
  const selectedApp = modalState?.app ?? null

  const isExportMode = useAIRiskReportExportMode()
  const rows = (appsData?.items ?? []).map((app) => ({ ...app, key: app.entityId || app.name }))
  const totalApps = appsData?.total ?? rows.length
  const appsGap = getDataGap(dataAvailability, [DATA_KEYS.DISCOVERED_AI_APPS])
  const usersGap = getDataGap(dataAvailability, [DATA_KEYS.DISCOVERED_AI_APP_USERS])
  const sharedGap = appsGap?.reason === usersGap?.reason ? appsGap : null
  const attributionGap = !appsGap && !usersGap ? gap : null

  const openApp = useCallback(
    (app) => setModalState({ app, openOnUsers: false, usageDateRange: getShadowAiDateRange() }),
    [],
  )
  const openAppUsers = useCallback(
    (app) => setModalState({ app, openOnUsers: true, usageDateRange: getShadowAiDateRange() }),
    [],
  )
  const closeApp = useCallback(() => setModalState(null), [])
  const columns = buildColumns(openApp, openAppUsers, isExportMode)
  const footer =
    totalApps > rows.length ? (
      <span className="small text-muted">
        <FormattedMessage
          id="AI_RISK_REPORT.THIRD.SHADOW_AI.SHOWING_TOP"
          values={{ shown: rows.length, total: totalApps }}
        />
      </span>
    ) : null

  if (sharedGap?.reason === DATA_AVAILABILITY_REASON.LICENSE_REQUIRED) {
    return (
      <SubBlock
        titleId="AI_RISK_REPORT.THIRD.SHADOW_AI.TITLE"
        subtitleId="AI_RISK_REPORT.THIRD.SHADOW_AI.SUBTITLE"
        score={score.value}
        tone={score.tone}
        gap={gap}
        defaultCollapsed={false}
      >
        <ShadowAiEmptyState columns={columns} dataAvailability={dataAvailability} />
      </SubBlock>
    )
  }

  if (sharedGap) {
    return (
      <SubBlock
        titleId="AI_RISK_REPORT.THIRD.SHADOW_AI.TITLE"
        subtitleId="AI_RISK_REPORT.THIRD.SHADOW_AI.SUBTITLE"
        score={score.value}
        tone={score.tone}
        gap={gap}
        defaultCollapsed={false}
      >
        <EmptyState
          messageId={getDiscoverUnavailableMessageId(sharedGap.reason)}
          values={{ dataType: 'Shadow AI' }}
          tone={TONE.NEUTRAL}
        />
      </SubBlock>
    )
  }

  return (
    <SubBlock
      titleId="AI_RISK_REPORT.THIRD.SHADOW_AI.TITLE"
      subtitleId="AI_RISK_REPORT.THIRD.SHADOW_AI.SUBTITLE"
      score={score.value}
      tone={score.tone}
      gap={gap}
      stats={!appsGap ? buildShadowAiStats(appsData, usersData, !usersGap) : null}
      defaultCollapsed={false}
    >
      <SubArea titleId="AI_RISK_REPORT.THIRD.SHADOW_AI.APPS_AREA_TITLE">
        <div className={styles.tableWrap}>
          <ReportTable
            columns={columns}
            rows={rows}
            footer={footer}
            empty={
              <EmptyState
                messageId={
                  appsGap ? getDiscoverUnavailableMessageId(appsGap.reason) : 'AI_RISK_REPORT.THIRD.SHADOW_AI.EMPTY'
                }
                values={{ dataType: 'Shadow AI' }}
                tone={appsGap ? TONE.NEUTRAL : TONE.OK}
              />
            }
          />
        </div>
        {selectedApp && !isExportMode && (
          <ApplicationDetailsModal
            show
            onHide={closeApp}
            application={selectedApp.url}
            applicationDataFromTable={{ key: getAppDisplayName(selectedApp), URL: selectedApp.url }}
            showAppDetails={!modalState?.openOnUsers}
            customer={selectedCustomer || undefined}
            usageDateRange={modalState?.usageDateRange}
            reload={closeApp}
          />
        )}
      </SubArea>
      <SubArea
        titleId="AI_RISK_REPORT.THIRD.SHADOW_AI_USERS.TITLE"
        descriptionId="AI_RISK_REPORT.THIRD.SHADOW_AI_USERS.SUBTITLE"
      >
        <ShadowAiUsers usersData={usersData} dataAvailability={dataAvailability} gap={attributionGap} />
      </SubArea>
    </SubBlock>
  )
}

ShadowAi.propTypes = {
  appsData: PropTypes.shape({
    total: PropTypes.number,
    unsanctionedTotal: PropTypes.number,
    pendingTotal: PropTypes.number,
    items: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  usersData: PropTypes.shape({
    total: PropTypes.number,
    discoverUsersTotal: PropTypes.number,
    items: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  score: PropTypes.shape({
    value: PropTypes.number,
    tone: PropTypes.oneOf(Object.values(TONE)),
  }),
  gap: PropTypes.shape({ reason: PropTypes.string, requiredLicense: PropTypes.string }),
  dataAvailability: PropTypes.arrayOf(PropTypes.shape({})),
}
